import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Plus } from "lucide-react";
import { getAdminQuestions, addQuestion, editQuestion, reorderItems } from "./plannerApi";

const inputCls = "bg-white border border-oth-line rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";
const TYPES = ["text", "textarea", "number", "phone", "email", "scale", "choice", "children"];

const QuestionRow = ({ q, onPatch, onMove, idx, total }) => {
    const [draft, setDraft] = useState(q);
    useEffect(() => setDraft(q), [q]);
    const commit = (patch) => onPatch(patch);
    return (
        <div className={`rounded-2xl border bg-white p-4 ${q.active ? "border-oth-line/70" : "border-red-200 opacity-70"}`} data-testid={`admin-q-${q.id}`}>
            <div className="flex items-start gap-3">
                <div className="flex flex-col pt-1 shrink-0">
                    <button onClick={() => idx > 0 && onMove(-1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`q-up-${q.id}`}><ChevronUp size={14} /></button>
                    <button onClick={() => idx < total - 1 && onMove(1)} className="text-oth-ink3 hover:text-oth-ink" data-testid={`q-down-${q.id}`}><ChevronDown size={14} /></button>
                </div>
                <div className="flex-1 space-y-2.5">
                    <input
                        className={`${inputCls} w-full`}
                        value={draft.label}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        onBlur={() => draft.label.trim() && draft.label !== q.label && commit({ label: draft.label.trim() })}
                        data-testid={`q-label-${q.id}`}
                    />
                    <div className="flex flex-wrap items-center gap-3 text-sm font-body">
                        <select className={inputCls} value={q.type} onChange={(e) => commit({ type: e.target.value })} data-testid={`q-type-${q.id}`}>
                            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {q.type === "scale" && (
                            <span className="inline-flex items-center gap-1.5">
                                <input type="number" className={`${inputCls} w-16`} value={q.min ?? 0} onChange={(e) => commit({ min: Number(e.target.value) })} data-testid={`q-min-${q.id}`} />
                                <span className="text-oth-ink3">to</span>
                                <input type="number" className={`${inputCls} w-16`} value={q.max ?? 10} onChange={(e) => commit({ max: Number(e.target.value) })} data-testid={`q-max-${q.id}`} />
                            </span>
                        )}
                        {q.type === "choice" && (
                            <input
                                className={`${inputCls} flex-1 min-w-[220px]`}
                                value={(draft.options || []).join(", ")}
                                onChange={(e) => setDraft({ ...draft, options: e.target.value.split(",").map((s) => s.trimStart()) })}
                                onBlur={() => commit({ options: (draft.options || []).map((s) => s.trim()).filter(Boolean) })}
                                placeholder="Options, comma separated"
                                data-testid={`q-options-${q.id}`}
                            />
                        )}
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={q.required} onChange={(e) => commit({ required: e.target.checked })} data-testid={`q-required-${q.id}`} />
                            Required
                        </label>
                        {q.maps_to && <span className="text-[10px] uppercase tracking-widest bg-[#eef3f8] text-oth-blue rounded-full px-2 py-0.5">used for contact: {q.maps_to}</span>}
                        <button onClick={() => commit({ active: !q.active })} className={`ml-auto text-xs ${q.active ? "text-oth-ink3 hover:text-red-600" : "text-emerald-700"}`} data-testid={`q-toggle-${q.id}`}>
                            {q.active ? "Remove from form" : "Restore"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PlannerQuestionsAdmin = () => {
    const [questions, setQuestions] = useState(null);
    const [newLabel, setNewLabel] = useState("");

    const load = useCallback(() => getAdminQuestions().then(setQuestions).catch(() => toast.error("Could not load questions.")), []);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, ok) => { try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); } };
    const move = (idx, dir) => {
        const ids = questions.map((x) => x.id);
        const j = idx + dir;
        [ids[idx], ids[j]] = [ids[j], ids[idx]];
        act(() => reorderItems("question", ids));
    };

    if (!questions) return <div className="font-editorial italic text-oth-ink3">Loading…</div>;

    return (
        <div className="space-y-4" data-testid="planner-questions-admin">
            <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Add question</div>
                <div className="flex gap-2">
                    <input className={`${inputCls} flex-1`} placeholder="New question label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="new-question-label" />
                    <button onClick={() => newLabel.trim() && act(() => addQuestion({ label: newLabel.trim(), type: "text" }).then(() => setNewLabel("")), "Question added.")} className="btn-pill btn-pill-blue text-sm" data-testid="new-question-add"><Plus size={14} /> Add</button>
                </div>
            </div>
            {questions.map((q, i) => (
                <QuestionRow key={q.id} q={q} idx={i} total={questions.length}
                    onPatch={(patch) => act(() => editQuestion(q.id, patch))}
                    onMove={(dir) => move(i, dir)} />
            ))}
            <p className="text-xs text-oth-ink3 font-body">Removed questions are only hidden from the form — answers already saved in plans are kept.</p>
        </div>
    );
};
